import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, MagnifyingGlass, Briefcase, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  listCandidateBarristers,
  offerAllocation,
  type CandidateBarrister,
  type CandidateConflictVerdict,
  type CandidateDiaryVerdict,
} from '@/lib/allocationsApi';
import { getApiErrorMessage } from '@/lib/api';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  caseId: string;
  caseLabel: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function OfferBriefModal({ caseId, caseLabel, open, onClose, onSuccess }: Props) {
  const [candidates, setCandidates] = useState<CandidateBarrister[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [clerkNote, setClerkNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    listCandidateBarristers(caseId)
      .then(setCandidates)
      .catch((err) => setError(getApiErrorMessage(err, 'Could not load barristers')))
      .finally(() => setLoading(false));
  }, [open, caseId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedId(null);
      setClerkNote('');
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const filtered = candidates.filter((c) => {
    if (!search.trim()) return true;
    const haystack =
      `${c.user.firstName} ${c.user.lastName} ${c.user.memberType}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });

  // Every candidate is scored against the same slot, so the first one carries the date.
  const workDate = candidates.find((c) => c.workDate)?.workDate ?? null;
  const checkedAgainst = workDate
    ? new Date(workDate).toLocaleDateString('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      })
    : null;

  const handleSubmit = async () => {
    if (!selectedId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await offerAllocation({
        caseId,
        barristerId: selectedId,
        clerkNote: clerkNote.trim() || undefined,
      });
      const picked = candidates.find((c) => c.id === selectedId);
      toast.success(
        `Brief offered to ${picked?.user.firstName} ${picked?.user.lastName}`,
      );
      onSuccess();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not send the offer'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="my-auto flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-brand-600 dark:text-brand-400" weight="duotone" />
              <h2 className="text-base font-semibold text-foreground">Offer brief to a barrister</h2>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">{caseLabel}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Search */}
        <div className="border-b border-border px-5 py-3">
          <div className="relative">
            <MagnifyingGlass
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              weight="bold"
            />
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or rank…"
              className="h-9 w-full rounded-md border border-border bg-card pl-9 pr-3 text-sm text-foreground transition placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </div>
        </div>

        {/* What the verdicts below were computed against. Without this the badges are
            ambiguous — "Diary clear" is only meaningful against a stated date. */}
        {!loading && filtered.length > 0 && (
          <div className="border-b border-border bg-muted/20 px-5 py-2 text-xs text-muted-foreground">
            {checkedAgainst ? (
              <>Diary and conflict checked against <span className="font-medium text-foreground">{checkedAgainst}</span></>
            ) : (
              <>No hearing date on this matter yet — diary and conflict could not be checked. Verdicts show as “not checked”.</>
            )}
          </div>
        )}

        {/* Candidates list */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">Loading barristers…</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              No matching barristers
            </div>
          ) : (
            <ul className="space-y-1">
              {filtered.map((c) => {
                const isSelected = selectedId === c.id;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => setSelectedId(c.id)}
                      className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-2.5 text-left transition ${
                        isSelected
                          ? 'bg-brand-50 ring-1 ring-brand-300 dark:bg-brand-900/30 dark:ring-brand-700/50'
                          : 'hover:bg-muted/40'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground">
                          {c.user.firstName} {c.user.lastName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {memberLabel(c.user.memberType)}
                          {c.callYear ? ` · Called ${c.callYear}` : ''}
                          {c.silkYear ? ` · Silk ${c.silkYear}` : ''}
                        </div>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${diaryCls(c.diaryVerdict)}`}>
                          {diaryLabel(c.diaryVerdict)}
                        </span>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${conflictCls(c.conflictVerdict)}`}>
                          {conflictLabel(c.conflictVerdict)}
                        </span>
                        {c.openOffersCount > 0 && (
                          <span
                            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                            title="Currently has open offers on other cases"
                          >
                            {c.openOffersCount} open
                          </span>
                        )}
                      </div>
                    </button>

                    {/* Disclosure — the clerk cannot disclose a clash they cannot see, so the
                        actual commitments are named inline, not hidden behind the badge. */}
                    {isSelected && (c.clashingCommitments.length > 0 || c.conflictReasons.length > 0) && (
                      <div className="mx-3 mb-2 mt-1 space-y-2 rounded-md border border-border bg-muted/30 px-3 py-2">
                        {c.clashingCommitments.length > 0 && (
                          <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              Already in the diary
                            </div>
                            <ul className="space-y-1">
                              {c.clashingCommitments.map((k) => (
                                <li key={k.id} className="flex items-start gap-2 text-xs text-foreground">
                                  <Warning
                                    className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${k.kind === 'overlap' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}
                                    weight="fill"
                                  />
                                  <span>
                                    <span className="font-medium">{k.title}</span>
                                    {' · '}
                                    {formatSlot(k.startAt, k.endAt)}
                                    {k.kind === 'adjacent' && k.gapMinutes !== undefined && (
                                      <span className="text-muted-foreground"> · {k.gapMinutes} min turnaround</span>
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {c.conflictReasons.length > 0 && (
                          <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              Conflict
                            </div>
                            <ul className="space-y-1">
                              {c.conflictReasons.map((r, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs text-red-700 dark:text-red-300">
                                  <Warning className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" weight="fill" />
                                  <span>{r}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Note */}
        <div className="border-t border-border px-5 py-3">
          <label className="mb-1 block text-xs font-medium text-foreground">
            Note for the barrister (optional)
          </label>
          <ExpandableTextarea
            label="Note for the barrister"
            value={clerkNote}
            onChange={setClerkNote}
            rows={2}
            placeholder="Why this brief is being offered to them — context they should know."
          />
        </div>

        {error && (
          <div className="border-t border-border px-5 py-2.5">
            <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger-bg px-3 py-2 text-xs text-danger">
              <Warning className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" weight="fill" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/30 px-5 py-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!selectedId || submitting}>
            {submitting ? 'Sending offer…' : 'Send offer'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function memberLabel(t: string): string {
  const map: Record<string, string> = {
    silk: "King's Counsel",
    junior: 'Junior',
    door_tenant: 'Door tenant',
  };
  return map[t] ?? t;
}

// Diary + conflict verdicts. 'unknown' is deliberately NEUTRAL, never green — the whole
// point of these badges is that an unchecked barrister must not read as free/clear.
function diaryCls(v: CandidateDiaryVerdict): string {
  switch (v) {
    case 'clear': return 'bg-success-bg text-success ring-success/20';
    case 'adjacent': return 'bg-warning-bg text-warning ring-warning/20';
    case 'overlap': return 'bg-danger-bg text-danger ring-danger/20';
    default: return 'bg-muted text-muted-foreground ring-border';
  }
}

function diaryLabel(v: CandidateDiaryVerdict): string {
  switch (v) {
    case 'clear': return 'Diary clear';
    case 'adjacent': return 'Tight turnaround';
    case 'overlap': return 'Diary clash';
    default: return 'Diary not checked';
  }
}

function conflictCls(v: CandidateConflictVerdict): string {
  switch (v) {
    case 'clear': return 'bg-success-bg text-success ring-success/20';
    case 'potential': return 'bg-warning-bg text-warning ring-warning/20';
    case 'critical': return 'bg-danger-bg text-danger ring-danger/20';
    default: return 'bg-muted text-muted-foreground ring-border';
  }
}

function conflictLabel(v: CandidateConflictVerdict): string {
  switch (v) {
    case 'clear': return 'No conflict';
    case 'potential': return 'Possible conflict';
    case 'critical': return 'Conflict';
    default: return 'Conflict not checked';
  }
}

/** "Tue 19 May, 10:30–13:00" — the clerk reads this out to the solicitor. */
function formatSlot(startAt: string, endAt: string): string {
  const s = new Date(startAt);
  const e = new Date(endAt);
  const day = s.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time(s)}–${time(e)}`;
}
